import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Modal, Row, Col, Input, Select, Form, Button, Divider } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AiOutlineSearch } from 'react-icons/ai';
import TableComponent from 'components/table-component';
import { SERVER_IP } from 'assets/Config';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { putApi } from 'redux/sagas/putApiSaga';
import { deleteApi } from 'redux/sagas/deleteApiSaga';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { API_STATUS } from 'constants/app-constants';
import { useI18n } from '../../hooks/useI18n';

const layer1FormCol = {
	labelCol: { span: 24 },
	wrapperCol: { span: 24 },
};

// --------------------- ServiceSelect ---------------------
const ServiceSelect = ({ value, onChange, openModal }) => {
	const services = useSelector((state) => state?.serviceRedux.services || []);
	const selectRef = useRef(null);

	return (
		<Select
			ref={selectRef}
			placeholder="Select Service"
			value={value}
			onChange={onChange}
			showSearch
			optionFilterProp="children"
			filterOption={(input, option) => option.children?.toLowerCase().includes(input.toLowerCase())}
			dropdownRender={(menu) => (
				<>
					{menu}
					<Divider style={{ margin: 0 }} />
					<div style={{ display: 'flex', flexWrap: 'nowrap', cursor: 'pointer' }} onClick={openModal}>
						<a style={{ flex: 'none', color: '#188dfa', padding: 8, display: 'block' }}>
							<PlusOutlined /> Service
						</a>
					</div>
				</>
			)}>
			{services.map((service) => (
				<Select.Option key={service.id} value={service.id}>
					{service.serviceName}
				</Select.Option>
			))}
		</Select>
	);
};

// --------------------- ServiceForm ---------------------
const ServiceForm = ({ form, onSubmit, loading, editData, onCancel }) => (
	<Form form={form} onFinish={onSubmit} layout="vertical" {...layer1FormCol}>
		<Form.Item
			label="Service Name"
			name="serviceName"
			rules={[{ required: true, message: 'This Field is Required!' }]}>
			<Input placeholder="Service Name" autoFocus />
		</Form.Item>
		<Form.Item>
			<Row justify="end" gutter={12}>
				<Col>
					<Button type="primary" htmlType="submit" loading={loading}>
						{editData ? 'Update' : 'Save'}
					</Button>
				</Col>
				<Col>
					<Button danger onClick={onCancel}>
						Cancel
					</Button>
				</Col>
			</Row>
		</Form.Item>
	</Form>
);

// --------------------- ServiceTable ---------------------
const ServiceTable = ({ data, loading, onEdit, onDelete }) => {
	const columns = useMemo(() => [
		{ title: 'Service Name', dataIndex: 'serviceName', key: 'serviceName' },
		{
			title: 'Actions',
			key: 'actions',
			align: 'center',
			render: (_, record) => (
				<Row justify="center" gutter={8}>
					<Col>
						<Button type="link" icon={<EditOutlined />} onClick={() => onEdit(record)} />
					</Col>
					<Col>
						<Button type="link" danger icon={<DeleteOutlined />} onClick={() => onDelete(record.id)} />
					</Col>
				</Row>
			),
		},
	], [onEdit, onDelete]);

	return (
		<TableComponent
			columns={columns}
			dataSource={data}
			rowKey="id"
			pagination={false}
			loading={loading}
			className="add-table"
		/>
	);
};

// --------------------- Main ServiceModal ---------------------
const ServiceModal = ({ value = null, onChange }) => {
	const [visible, setVisible] = useState(false);
	const [addFormVisible, setAddFormVisible] = useState(false);
	const [editData, setEditData] = useState(null);
	const [searchKey, setSearchKey] = useState('');
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const { t } = useI18n();

	const globalRedux = useSelector((state) => state.globalRedux);
	const services = useSelector((state) => state?.serviceRedux.services || []);
	const branchId = globalRedux?.selectedBranch?.id;

	const loading = globalRedux.apiStatus.GET_SERVICES === API_STATUS.PENDING;
	const buttonLoading =
		globalRedux.apiStatus.ADD_SERVICE === API_STATUS.PENDING || globalRedux.apiStatus.EDIT_SERVICE === API_STATUS.PENDING;

	const getServices = useCallback(() => {
		const url = `${SERVER_IP}api/services?branchId=${branchId}`;
		dispatch(getApi('GET_SERVICES', url));
	}, [branchId, dispatch]);

	useEffect(() => {
		getServices();
	}, [getServices]);

	useEffect(() => {
		if (!addFormVisible) {
			form.resetFields();
			setEditData(null);
		}
	}, [addFormVisible]);

	useEffect(() => {
		const status = globalRedux.apiStatus;
		if (status.ADD_SERVICE === 'SUCCESS' || status.EDIT_SERVICE === 'SUCCESS') {
			dispatch(resetApiStatus(editData ? 'EDIT_SERVICE' : 'ADD_SERVICE'));
			getServices();
			setAddFormVisible(false);
		}
		if (status.DELETE_SERVICE === 'SUCCESS') {
			dispatch(resetApiStatus('DELETE_SERVICE'));
			getServices();
		}
	}, [globalRedux.apiStatus]);

	const handleSubmit = (values) => {
		const payload = { ...values, branchId };
		if (editData) {
			dispatch(putApi(payload, 'EDIT_SERVICE', `${SERVER_IP}api/services/${editData.id}`));
		} else {
			dispatch(postApi(payload, 'ADD_SERVICE'));
		}
	};

	const handleDelete = (id) => {
		dispatch(deleteApi('DELETE_SERVICE', `${SERVER_IP}api/services/${id}`));
	};

	const filteredServices = useMemo(() => {
		return services.filter((s) => s?.serviceName?.toLowerCase().includes(searchKey.toLowerCase()));
	}, [services, searchKey]);

	return (
		<>
			<ServiceSelect value={value} onChange={onChange} openModal={() => setVisible(true)} />

			<Modal
				title="Manage Services"
				open={visible}
				onCancel={() => setVisible(false)}
				footer={null}
				destroyOnClose
				width="40%"
				style={{ top: 20 }}>
				{addFormVisible ? (
					<ServiceForm
						form={form}
						onSubmit={handleSubmit}
						loading={buttonLoading}
						editData={editData}
						onCancel={() => setAddFormVisible(false)}
					/>
				) : (
					<>
						<Row justify="space-between" style={{ marginBottom: 12 }}>
							<Col span={16}>
								<Input
									placeholder="Search Service"
									value={searchKey}
									onChange={(e) => setSearchKey(e.target.value)}
									suffix={<AiOutlineSearch />}
								/>
							</Col>
							<Col>
								<Button type="primary" onClick={() => setAddFormVisible(true)}>
									{t('services.create_service')}
								</Button>
							</Col>
						</Row>
						<ServiceTable
							data={filteredServices}
							loading={loading}
							onEdit={(record) => {
								setEditData(record);
								form.setFieldsValue({ serviceName: record.serviceName });
								setAddFormVisible(true);
							}}
							onDelete={handleDelete}
						/>
					</>
				)}
			</Modal>
		</>
	);
};

export default ServiceModal;
